/**
 * Linking wrapper — `openURL` only.
 *
 * Reason this exists:
 *
 *   In E2E runs against the real device/simulator, calling React Native's
 *   `Linking.openURL` actually launches Safari (or the platform browser
 *   / store app). That backgrounds Fluencia, every subsequent UI step
 *   fails because the test driver can't find Fluencia testIDs in the
 *   foreground app, and the run cascades to fail after the very first
 *   action-button tap that fires an external link.
 *
 *   Two things matter to E2E:
 *     1. The renderer's `onPress` ran (the message-store's
 *        `dismissMessage` POST is the wire-level proof of this for
 *        banner + dismissable modal).
 *     2. WHAT URL the renderer would have opened (server-side bugs
 *        like "iOS App Store URL sent to an Android user" want this).
 *
 *   We capture (2) by routing `openURL` through `notifyLinking` in the
 *   storage observer — a new `external-nav` surface in the unified
 *   call log. The runner then asserts via `ExpectLink(url)`. The
 *   platform `Linking.openURL` itself is intentionally suppressed in
 *   E2E so Fluencia stays foregrounded for the rest of the branch.
 *
 *   Production paths are unchanged — `__DEV__ && E2E_MODE` is false,
 *   the platform call runs normally.
 */

import { Linking } from 'react-native';
import { E2E_MODE } from '../config/constants';
import { notifyLinking } from '../dev/storage-observer';

/**
 * Open an external URL. Drop-in replacement for `Linking.openURL` —
 * same signature, same return semantics. Use this instead of importing
 * `Linking` directly from `react-native` so E2E can suppress the
 * platform handoff and assert the URL via the dev observer.
 *
 * Returns `true` on success. In E2E mode, returns `true` immediately
 * after recording the URL (no platform call). In production, returns
 * the platform's resolved boolean from `Linking.openURL`.
 *
 * Errors from the platform call are swallowed in production — that's
 * the same behavior as `Linking.openURL(...).catch(() => {})` which is
 * what most call sites today already do. Bubble them up only if a
 * caller specifically wants to react (none do today).
 */
export async function openURL(url: string): Promise<boolean> {
  if (__DEV__ && E2E_MODE) {
    // Record the would-be URL and short-circuit. Notification is
    // best-effort — the dev observer is dev-only, never on production
    // paths, so a failure here means we're in a weird state but the
    // app doesn't depend on it.
    try {
      notifyLinking(url);
    } catch {
      /* observer not initialized — drop the event */
    }
    return true;
  }
  try {
    await Linking.openURL(url);
    return true;
  } catch {
    return false;
  }
}
